import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrdersCatalog } from '../context/OrdersCatalogContext';
import { formatOrderDate, orderMatchesLocalDate, type Order } from '../types/order';

function countProductsInOrder(order: Order): number {
  return order.lines.reduce((sum, line) => sum + line.quantity, 0);
}

const ITEMS_PER_PAGE = 20;

export default function OrdersListPage() {
  const { orders } = useOrdersCatalog();
  const [filterDni, setFilterDni] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [page, setPage] = useState(1);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  const filteredOrders = useMemo(() => {
    const dniQ = filterDni.trim().toLowerCase();
    const nameQ = filterName.trim().toLowerCase();
    return orders.filter((order) => {
      const dni = (order.clientDni || '').toLowerCase();
      const name = order.clientName.toLowerCase();
      const matchesDni = !dniQ || dni.includes(dniQ);
      const matchesName = !nameQ || name.includes(nameQ);
      const matchesDate = orderMatchesLocalDate(order.createdAt, filterDate);
      return matchesDni && matchesName && matchesDate;
    });
  }, [orders, filterDni, filterName, filterDate]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  const paginatedOrders = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, safePage]);

  const resetFiltersPage = () => {
    setPage(1);
  };

  return (
    <section className="card content-card">
      <header className="module-header">
        <h2>Modulo de Ordenes</h2>
        <p className="subtitle">Listado de pedidos y creacion de nuevos pedidos.</p>
        <Link to="/admin/ordenes/nuevo" className="button-link add-medicine-button">
          Nuevo pedido
        </Link>
      </header>

      <div className="medicine-filters card-section">
        <h3 className="section-title">Filtros</h3>
        <div className="order-list-filters">
          <div className="field-group">
            <label htmlFor="filter-dni">DNI</label>
            <input
              id="filter-dni"
              type="text"
              placeholder="Filtrar por DNI..."
              value={filterDni}
              onChange={(e) => {
                setFilterDni(e.target.value);
                resetFiltersPage();
              }}
            />
          </div>
          <div className="field-group">
            <label htmlFor="filter-name">Nombres</label>
            <input
              id="filter-name"
              type="text"
              placeholder="Filtrar por nombre del cliente..."
              value={filterName}
              onChange={(e) => {
                setFilterName(e.target.value);
                resetFiltersPage();
              }}
            />
          </div>
          <div className="field-group">
            <label htmlFor="filter-date">Fecha de pedido</label>
            <input
              id="filter-date"
              type="date"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                resetFiltersPage();
              }}
            />
          </div>
        </div>
      </div>

      <div className="table-wrapper card-section">
        <div className="table-header">
          <h3 className="section-title">Pedidos</h3>
          <span className="subtitle">
            Mostrando {paginatedOrders.length} de {filteredOrders.length}
          </span>
        </div>
        <table className="medicine-table">
          <thead>
            <tr>
              <th>DNI</th>
              <th>Nombre cliente</th>
              <th>Fecha de pedido</th>
              <th>Total</th>
              <th>Productos</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order) => (
              <tr key={order.id}>
                <td>{order.clientDni || '—'}</td>
                <td>{order.clientName}</td>
                <td>{formatOrderDate(order.createdAt)}</td>
                <td>S/. {order.total.toFixed(2)}</td>
                <td>{countProductsInOrder(order)}</td>
                <td>
                  <button type="button" className="table-action-button" onClick={() => setDetailOrder(order)}>
                    Ver detalle
                  </button>
                </td>
              </tr>
            ))}
            {paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-row">
                  No hay pedidos con los filtros actuales.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button type="button" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={safePage === 1}>
          Anterior
        </button>
        <span>
          Pagina {safePage} de {totalPages} ({filteredOrders.length} resultados)
        </span>
        <button type="button" onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={safePage === totalPages}>
          Siguiente
        </button>
      </div>

      {detailOrder ? (
        <div className="modal-overlay" onClick={() => setDetailOrder(null)} role="presentation">
          <div
            className="modal-content modal-content-order-detail"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header">
              <h3>Detalle del pedido</h3>
              <button type="button" className="modal-close" onClick={() => setDetailOrder(null)}>
                x
              </button>
            </div>

            <div className="order-detail-body">
              <div className="order-detail-section">
                <h4 className="order-detail-heading">Cliente</h4>
                <dl className="order-detail-dl">
                  <div>
                    <dt>DNI</dt>
                    <dd>{detailOrder.clientDni || '—'}</dd>
                  </div>
                  <div>
                    <dt>Nombre</dt>
                    <dd>{detailOrder.clientName}</dd>
                  </div>
                  <div className="order-detail-full">
                    <dt>Direccion</dt>
                    <dd>{detailOrder.address}</dd>
                  </div>
                  <div className="order-detail-full">
                    <dt>Referencia</dt>
                    <dd>{detailOrder.reference}</dd>
                  </div>
                  <div>
                    <dt>Fecha del pedido</dt>
                    <dd>{formatOrderDate(detailOrder.createdAt)}</dd>
                  </div>
                  <div>
                    <dt>Productos (unidades)</dt>
                    <dd>{countProductsInOrder(detailOrder)}</dd>
                  </div>
                </dl>
              </div>

              <div className="order-detail-section">
                <h4 className="order-detail-heading">Productos</h4>
                <div className="order-detail-table-wrap">
                  <table className="medicine-table order-detail-products-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Cantidad</th>
                        <th>P. unitario</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailOrder.lines.map((line) => (
                        <tr key={line.id}>
                          <td>{line.productName}</td>
                          <td>{line.quantity}</td>
                          <td>S/. {line.unitPrice.toFixed(2)}</td>
                          <td>S/. {line.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="order-detail-total">
                  <strong>Total pedido</strong>
                  <span>S/. {detailOrder.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="medicine-form-actions order-detail-footer">
              <button type="button" className="secondary-button" onClick={() => setDetailOrder(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
